<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) +
            " " +
            $t("storage_provider.title")
          }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Name -->
          <validation-provider
            #default="validationContext"
            :name="$t('storage_provider.name')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('storage_provider.name')"
              :label-for="$t('storage_provider.name')"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('storage_provider.description')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('storage_provider.description')"
              label-for="description"
            >
              <b-form-input
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- storage provider -->
          <validation-provider
            #default="validationContext"
            :name="$t('storage_provider.type')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('storage_provider.type')"
              :label-for="$t('storage_provider.type')"
            >
              <v-select
                v-model="itemData.type"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="storageProvider"
                :reduce="(val) => val.value"
                input-id="storage_service"
                :clearable="false"
                @input="selectStorageType"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div v-if="isLocal">
            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.folder')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.folder')"
                label-for="folder"
              >
                <b-form-input
                  id="folder"
                  v-model="config.folder"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>

          <div v-if="isAWS || isHuawei">
            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.bucket_name')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.bucket_name')"
                label-for="bucket_name"
              >
                <b-form-input
                  id="bucket_name"
                  v-model="config.bucket_name"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.region')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.region')"
                label-for="region"
              >
                <b-form-input
                  id="region"
                  v-model="config.region"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.access_key_id')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.access_key_id')"
                label-for="access_key_id"
              >
                <b-form-input
                  id="access_key_id"
                  v-model="config.access_key_id"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.secret_access_key')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.secret_access_key')"
                label-for="secret_access_key"
              >
                <b-form-input
                  id="secret_access_key"
                  v-model="config.secret_access_key"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-if="isHuawei"
              #default="validationContext"
              :name="$t('storage_provider.endpoint')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.endpoint')"
                label-for="endpoint"
              >
                <b-form-input
                  id="endpoint"
                  v-model="config.endpoint"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>

          <div v-if="isAzure">
            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.account_name')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.account_name')"
                :label-for="$t('storage_provider.account_name')"
              >
                <b-form-input
                  id="account_name"
                  v-model="config.account_name"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.account_key')"
              :rules="isRequired"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.account_key')"
                :label-for="$t('storage_provider.account_key')"
              >
                <b-form-input
                  id="account_key"
                  v-model="config.account_key"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.container_name')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.container_name')"
                :label-for="$t('storage_provider.container_name')"
              >
                <b-form-input
                  id="container_name"
                  v-model="config.container_name"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.endpoint')"
              rules="required"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.endpoint')"
                :label-for="$t('storage_provider.endpoint')"
              >
                <b-form-input
                  id="endpoint"
                  v-model="config.endpoint"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              :name="$t('storage_provider.sas_token')"
              :rules="isRequired"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('storage_provider.sas_token')"
                :label-for="$t('storage_provider.sas_token')"
              >
                <b-form-input
                  id="sas_token"
                  v-model="config.sas_token"
                  :state="getValidationState(validationContext)"
                  trim
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BImg,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    BImg,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    storageProvider: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
    };
  },
  setup(props, context) {
    const toast = useToast();
    const { t } = useI18nUtils();
    const trans = {
      "messages.edit_save.successfully_created": t(
        "messages.edit_save.successfully_created"
      ),
      "messages.edit_save.updated_successfully": t(
        "messages.edit_save.updated_successfully"
      ),
    };
    const blankData = {
      name: "",
      description: "",
      type: "",
    };
    const isLocal = ref(false);
    const isAWS = ref(false);
    const isHuawei = ref(false);
    const isAzure = ref(false);
    const config = ref({});

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const onSubmit = () => {
      let dispatch;
      itemData.value.config = config.value;

      if (props.isAdd) {
        dispatch = store.dispatch("storage_provider/add", itemData.value);
      } else {
        const req = {
          id: itemData.value.id,
          params: {
            id: itemData.value.id,
            name: itemData.value.name,
            description: itemData.value.description,
            type: itemData.value.type,
            config: itemData.value.config,
          },
        };

        dispatch = store.dispatch("storage_provider/edit", req);
      }

      dispatch
        .then((response) => {
          if (response.status == "success") {
            toast({
              component: ToastificationContent,
              props: {
                title: props.isAdd
                  ? trans["messages.edit_save.successfully_created"]
                  : trans["messages.edit_save.updated_successfully"],
                icon: "CheckIcon",
                variant: "success",
              },
            });
            context.emit("refetch-data");
            context.emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: context.parent.$i18n.t("storage_provider.message.error"),
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: context.parent.$i18n.t("storage_provider.message.error"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const loadData = () => {
      config.value = {};
      isLocal.value = false;
      isAWS.value = false;
      isHuawei.value = false;
      isAzure.value = false;

      if (!props.isAdd) {
        itemData.value = Object.assign({}, props.itemEdit);
        config.value = { ...itemData.value.config };
        let type = itemData.value.type;

        applyTypeConfiguration(type);
      }
    };

    const selectStorageType = (value) => {
      config.value = {};
      isLocal.value = false;
      isAWS.value = false;
      isHuawei.value = false;
      isAzure.value = false;

      applyTypeConfiguration(value);
    };

    const applyTypeConfiguration = (value) => {
      switch (value) {
        case "local":
          isLocal.value = true;
          break;
        case "aws":
          isAWS.value = true;
          break;
        case "huawei":
          isHuawei.value = true;
          break;
        case "azure":
          isAzure.value = true;
          break;
      }
    };

    const isRequired = computed(() => {
      if (config.value.sas_token || config.value.account_key) {
        return "";
      }
      return "required";
    });

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      selectStorageType,
      config,
      isLocal,
      isAWS,
      isHuawei,
      isAzure,
      isRequired,
    };
  },
};
</script>

<style lang="scss">
</style>
